import React, { useEffect, useRef, useState } from 'react';

import Moveable from 'react-moveable';

import '../../../css/Tools.scss';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

import { register } from '../../../i18n';

const t2 = register('AriaLabels');

/** NOTE: `CustomaryRuler` is now typically referred to as 'Imperial Ruler' from a UI/UX standpoint */
const CustomaryRuler = () => {
  const [target, setTarget] = useState();
  // eslint-disable-next-line no-unused-vars
  const [frame, _setFrame] = useState({
    translate: [0, 0],
    rotate: 0,
    transformOrigin: '50% 50%',
  });

  const moveableRef = useRef();
  const verticalRef = useRef();
  const horizontalRef = useRef();
  const containerRef = useRef();

  useAccessibilityClick(verticalRef, (_event) => {
    moveableRef.current.moveable.request('rotatable', { rotate: 90 }, true);
  });

  useAccessibilityClick(horizontalRef, (_event) => {
    moveableRef.current.moveable.request('rotatable', { rotate: 0 }, true);
  });

  useAccessibilityClick(containerRef, (_event) => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
  });

  // Handle keyboard navigation for moving the ruler
  const handleKeyDown = (event) => {
    if (!moveableRef.current) return;

    const STEP_SIZE = 10; // pixels to move per arrow key press
    const SHIFT_STEP_SIZE = 50; // larger step when shift is held
    
    let deltaX = 0;
    let deltaY = 0;
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        deltaX = -STEP_SIZE;
        if (event.shiftKey) deltaX = -SHIFT_STEP_SIZE;
        break;
      case 'ArrowRight':
        event.preventDefault();
        deltaX = STEP_SIZE;
        if (event.shiftKey) deltaX = SHIFT_STEP_SIZE;
        break;
      case 'ArrowUp':
        event.preventDefault();
        deltaY = -STEP_SIZE;
        if (event.shiftKey) deltaY = -SHIFT_STEP_SIZE;
        break;
      case 'ArrowDown':
        event.preventDefault();
        deltaY = STEP_SIZE;
        if (event.shiftKey) deltaY = SHIFT_STEP_SIZE;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        // Trigger rotation to horizontal (0 degrees)
        moveableRef.current.moveable.request('rotatable', { rotate: 0 }, true);
        return;
      default:
        return;
    }

    // Update the frame position
    const newTranslate = [
      frame.translate[0] + deltaX,
      frame.translate[1] + deltaY
    ];
    
    // Update the frame state directly (since we're not using setFrame)
    frame.translate = newTranslate;

    // Update the moveable component
    moveableRef.current.moveable.request('draggable', {
      translate: newTranslate
    }, true);
  };

  // CF-4140: Added to fix bug that continued drag even after mouse click.
  const stopDrag = (_event) => {
    moveableRef.current.moveable.stopDrag();
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
    setTarget(document.querySelector('.customaryRulerTarget'));
    document.addEventListener('mouseup', stopDrag);
    
    // Add keyboard event listener
    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('mouseup', stopDrag);
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, []);

  return (
    <div className='customaryRulerContainer' tabIndex={-1}>
      <div className='customaryRulerTarget' id='customaryRulerTool' ref={containerRef} aria-label={t2('customaryRulerTool', 'Customary ruler tool. Use arrow keys to move, Enter or Space to rotate to horizontal')} role='application' tabIndex={0}>
        <button 
          ref={horizontalRef} 
          aria-label={t2('rotateCustomaryRulerHorizontal')} 
          id='customaryRulerToolSnapHorizontalButton' 
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              moveableRef.current.moveable.request('rotatable', { rotate: 0 }, true);
            }
          }}
        />
        <button 
          ref={verticalRef} 
          aria-label={t2('rotateCustomaryRulerVertical')} 
          id='customaryRulerToolSnapVerticalButton' 
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              moveableRef.current.moveable.request('rotatable', { rotate: 90 }, true);
            }
          }}
        />
        <div id='customaryRulerToolRotator'>
          <div id='customaryRulerToolRotateHandle' />
        </div>
      </div>
      <Moveable
        ref={moveableRef}
        draggable={true}
        onDrag={(event) => {
          frame.translate = event.beforeTranslate;
        }}
        onDragOrigin={(event) => {
          frame.translate = event.drag.beforeTranslate;
          frame.transformOrigin = event.transformOrigin;
        }}
        onDragOriginStart={(event) => {
          event.dragStart && event.dragStart.set(frame.translate);
        }}
        onDragStart={(event) => {
          event.set(frame.translate);
        }}
        onRender={(event) => {
          const { translate, rotate, transformOrigin } = frame;
          event.target.style.transformOrigin = transformOrigin;
          event.target.style.transform = `translate(${translate[0]}px, ${translate[1]}px)`
            + ` rotate(${rotate}deg)`;
        }}
        onRotate={(event) => {
          frame.rotate = event.beforeRotate;
        }}
        onRotateStart={(event) => {
          event.set(frame.rotate);
        }}
        origin={false}
        originDraggable={true}
        originRelative={true}
        padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
        rotatable={true}
        rotationPosition='left'
        startDragRotate={0}
        target={target}
        throttleDrag={0}
        throttleDragRotate={0}
        throttleRotate={0}
        zoom={1}
      />
    </div>
  );
};

export default CustomaryRuler;
